// frontend/admin-portal/src/pages/Customers.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  formatMoney,
  Input,
} from "@fiberhome/shared-ui";
import { customerService } from "../services/customerService";
import CustomerModal from "../components/orders/CustomerModal";
import { useSettings } from "../contexts/SettingsContext";

export default function Customers() {
  const navigate = useNavigate();
  const { get } = useSettings();
  const currency = get("finance.baseCurrency", "USD");

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    type: "",
    isActive: "",
    hasBalance: false,
    hasOverdue: false,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await customerService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (c) => {
    setEditing(c);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setEditing(null);
    load();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      await customerService.remove(c.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Customer",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.code}
            {r.companyName ? ` · ${r.companyName}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: (r) => (
        <Badge
          variant={
            r.type === "business"
              ? "primary"
              : r.type === "government"
                ? "info"
                : "default"
          }
        >
          {r.type}
        </Badge>
      ),
    },
    {
      key: "creditBalance",
      label: "Balance",
      render: (r) => {
        const bal = Number(r.creditBalance) || 0;
        return (
          <span
            style={{
              fontWeight: 600,
              color: bal > 0 ? "#dc3545" : bal < 0 ? "#0d6efd" : "#198754",
            }}
          >
            {formatMoney(Math.abs(bal), { symbol: currency })}
            {bal > 0 && (
              <span
                className="text-muted"
                style={{ fontSize: 10, marginLeft: 4 }}
              >
                owed
              </span>
            )}
            {bal < 0 && (
              <span
                className="text-muted"
                style={{ fontSize: 10, marginLeft: 4 }}
              >
                credit
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: "contact",
      label: "Contact",
      render: (r) => (
        <div style={{ fontSize: 13 }}>
          {r.phone && <div>{r.phone}</div>}
          {r.email && <div className="text-muted">{r.email}</div>}
          {!r.phone && !r.email && <span className="text-muted">—</span>}
        </div>
      ),
    },
    {
      key: "location",
      label: "Location",
      render: (r) =>
        [r.city, r.country].filter(Boolean).join(", ") || (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "isActive",
      label: "Status",
      render: (r) => (
        <Badge variant={r.isActive ? "success" : "default"}>
          {r.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 220,
      render: (r) => (
        <div className="d-flex gap-1">
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/customers/${r.id}`)}
          >
            View
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleEdit(r)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Individuals, businesses, and government clients"
        breadcrumbs={["Home", "Sales", "Customers"]}
        actions={
          <Button variant="primary" onClick={handleNew}>
            + New Customer
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <div className="fh-supplier-filters">
          <Input
            placeholder="Search name, code, phone, email..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value, page: 1 })
            }
          />
          <select
            className="fh-input"
            value={filters.type}
            onChange={(e) =>
              setFilters({ ...filters, type: e.target.value, page: 1 })
            }
          >
            <option value="">All types</option>
            <option value="individual">Individual</option>
            <option value="business">Business</option>
            <option value="government">Government</option>
          </select>
          <select
            className="fh-input"
            value={filters.isActive}
            onChange={(e) =>
              setFilters({ ...filters, isActive: e.target.value, page: 1 })
            }
          >
            <option value="">All statuses</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
          <label className="fh-checkbox" style={{ whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={filters.hasBalance}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  hasBalance: e.target.checked,
                  page: 1,
                })
              }
            />
            <span>Only with balance</span>
          </label>
          <label className="fh-checkbox" style={{ whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={filters.hasOverdue}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  hasOverdue: e.target.checked,
                  page: 1,
                })
              }
            />
            <span>Only overdue</span>
          </label>
        </div>
      </Card>

      <Card
        title={`Customers (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar__pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Prev
            </Button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Page {pagination.page} / {pagination.pages || 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No customers yet"
        />
      </Card>

      <CustomerModal
        open={modalOpen}
        customer={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />
    </>
  );
}
